import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  Table,
  useToast,
  type Column,
} from "../../components/ui";
import {
  adminService,
  type AdminOrganization,
  type AdminUser,
  type RoleOption,
} from "../../services/admin.service";
import type { Role } from "../../services/auth.service";
import { formatRelative } from "../../lib/format";

interface FormState {
  name: string;
  email: string;
  password: string;
  role: Role | "";
  organization_id: string;
  is_active: boolean;
}

const emptyForm: FormState = {
  name: "",
  email: "",
  password: "",
  role: "",
  organization_id: "",
  is_active: true,
};

/**
 * Gestão de usuários dentro da hierarquia do usuário autenticado.
 * O backend recorta a lista e recusa papéis acima do ator — a UI apenas reflete.
 */
export default function UsersPage() {
  const toast = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<AdminUser | null>(null);

  const load = useCallback(
    async (term = "") => {
      setLoading(true);
      try {
        const [list, orgs] = await Promise.all([
          adminService.users(term || undefined),
          adminService.organizations(),
        ]);
        setUsers(list.data);
        setRoles(list.meta?.assignable_roles ?? []);
        setOrganizations(orgs.data);
        setError(null);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setEditing(null);
    setForm({
      ...emptyForm,
      role: roles[0]?.value ?? "",
      organization_id: organizations[0]?.id ?? "",
    });
    setDialogOpen(true);
  }

  function openEdit(user: AdminUser) {
    setEditing(user);
    setForm({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
      organization_id: user.organizations?.[0]?.id ?? "",
      is_active: user.is_active,
    });
    setDialogOpen(true);
  }

  async function save() {
    setSaving(true);
    try {
      if (editing) {
        await adminService.updateUser(editing.id, {
          name: form.name,
          email: form.email,
          role: form.role as Role,
          is_active: form.is_active,
          ...(form.password ? { password: form.password } : {}),
        });
        toast.success("Usuário atualizado.");
      } else {
        await adminService.createUser({
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role as Role,
          organization_id: form.organization_id,
          is_active: form.is_active,
        });
        toast.success("Usuário criado.");
      }
      setDialogOpen(false);
      await load(search);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!removing) return;
    try {
      await adminService.deleteUser(removing.id);
      toast.success("Usuário removido.");
      await load(search);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setRemoving(null);
    }
  }

  const columns: Column<AdminUser>[] = [
    { key: "name", header: "Nome", render: (u) => u.name },
    { key: "email", header: "E-mail", render: (u) => u.email },
    { key: "role", header: "Papel", render: (u) => <Badge>{u.role_label}</Badge> },
    {
      key: "org",
      header: "Organização",
      render: (u) => u.organizations?.map((o) => o.name).join(", ") || "—",
    },
    {
      key: "status",
      header: "Status",
      render: (u) => (
        <Badge tone={u.is_active ? "ok" : "warn"}>{u.is_active ? "Ativo" : "Inativo"}</Badge>
      ),
    },
    { key: "last", header: "Último acesso", render: (u) => formatRelative(u.last_login_at) },
    {
      key: "actions",
      header: "",
      render: (u) => (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => openEdit(u)}>
            Editar
          </Button>
          <Button variant="ghost" onClick={() => setRemoving(u)}>
            Remover
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Usuários"
        description="Somente usuários abaixo do seu nível na hierarquia."
        actions={
          roles.length > 0 ? <Button onClick={openCreate}>Novo usuário</Button> : undefined
        }
      />

      <Card>
        <div className="mb-4 flex gap-2">
          <Input
            placeholder="Buscar por nome ou e-mail"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void load(search);
            }}
          />
          <Button variant="secondary" onClick={() => void load(search)}>
            Buscar
          </Button>
        </div>

        {loading ? (
          <SkeletonRows rows={5} />
        ) : error ? (
          <EmptyState title="Não foi possível carregar" description={error} />
        ) : users.length === 0 ? (
          <EmptyState
            title="Nenhum usuário no seu escopo"
            description="Crie o primeiro usuário da sua estrutura."
          />
        ) : (
          <Table rows={users} columns={columns} rowKey={(u) => u.id} />
        )}
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Editar usuário" : "Novo usuário"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={() => void save()} loading={saving}>
              Salvar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="E-mail"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Input
            label={editing ? "Nova senha (opcional)" : "Senha"}
            type="password"
            value={form.password}
            hint="Mínimo de 12 caracteres."
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <Select
            label="Papel"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
            options={roles.map((r) => ({ value: r.value, label: r.label }))}
          />
          {!editing && (
            <Select
              label="Organização"
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
              options={organizations.map((o) => ({ value: o.id, label: `${o.name} (${o.tier})` }))}
            />
          )}
          <Select
            label="Status"
            value={form.is_active ? "1" : "0"}
            onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })}
            options={[
              { value: "1", label: "Ativo" },
              { value: "0", label: "Inativo" },
            ]}
          />
        </div>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        title="Remover usuário"
        description={`Remover ${removing?.name}? O acesso é revogado imediatamente.`}
        confirmLabel="Remover"
        onCancel={() => setRemoving(null)}
        onConfirm={() => void remove()}
      />
    </>
  );
}
